import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { sprintf } from "sprintf-js";
import {
  Box,
  Grid,
  Chip,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  Typography,
  CircularProgress,
  Button,
  Checkbox
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DataGrid,
  GridToolbar,
  useGridApiRef,
  GridActionsCellItem,
  renderActionsCell,
  GridPagination
} from "@mui/x-data-grid";
import * as MUIICon from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintIcon from "@mui/icons-material/Print";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import { baseURL } from "../../api";
import { serverAddress } from "../../config";
import { Global_Data } from "../../globalData/GlobalData";
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import { DataGridPro } from "@mui/x-data-grid-pro";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import CustomAlert from "../../component/AlertComponent/Alert";
import { buildDataGridCellId } from "./cellId";
import {
  createBulkOperationId,
  hasBulkSelection,
  isBulkActionAllowedForRows,
  isBulkProgressEnabled,
  resolveBulkRequiredActivity,
  resolveBulkWorkflowVerification
} from "./bulkActionAccess";
import {
    assignStableDataGridRowIds,
    mergeDataGridPageSelection,
    resolveDataGridRowId,
    resolveRestoredDataGridFilter,
    retainSelectedVerificationStatuses
} from "./dataGridSelection";
import { resolveDataGridRowAction } from "./rowVariables";

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  backgroundColor:
    theme.palette.mode === "light" ? "rgba(255, 255, 255, 0.9)" : "rgba(18, 18, 18, 0.9)"
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: 13,
    border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
    padding: '0 4px',
  },
}));

function getNestedValue(obj, path) {
  if (!obj || !path) return null;

  return path
    .split(".")
    .reduce((acc, part) => acc?.[part], obj);
}

const ImageCell = React.memo(function ImageCell({
  imageIds,
  format,
  token
}) {
  const [src, setSrc] = React.useState(null);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    let objectURL = null;
    let cancelled = false;

    async function loadImage() {
      try {
        setSrc(null);
        setHasError(false);

        if (!imageIds || !format) {
          setHasError(true);
          return;
        }

        const imageValue = String(imageIds).trim();

        if (!imageValue) {
          setHasError(true);
          return;
        }

        const response = await fetch(`${baseURL}${format}${imageValue}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          setHasError(true);
          return;
        }

        const blob = await response.blob();

        if (!blob || blob.size === 0) {
          setHasError(true);
          return;
        }

        objectURL = URL.createObjectURL(blob);

        if (!cancelled) {
          setSrc(objectURL);
        }
      } catch (err) {
        console.error(err);

        if (!cancelled) {
          setHasError(true);
        }
      }
    }

    loadImage();

    return () => {
      cancelled = true;

      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [imageIds, format, token]);

  if (hasError || !src) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      style={{
        width: 56,
        height: 56,
        objectFit: "contain",
        display: "block"
      }}
    />
  );
});

const AvatarCell = React.memo(function AvatarCell({
  row,
  item,
  token,
  stringAvatar
}) {
  const [src, setSrc] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  const [imageFormat, fallbackPath] = String(item?.sFormat || "").split(":");

  const imageId = getNestedValue(row, item?.sColumnID);
  const fallbackText = getNestedValue(row, fallbackPath) || "";

  React.useEffect(() => {
    let objectURL = null;
    let cancelled = false;

    async function loadAvatarImage() {
      try {
        setSrc(null);
        setFailed(false);

        if (!imageId || !imageFormat) {
          setFailed(true);
          return;
        }

        const firstImageId = String(imageId)
          .split(",")[0]
          ?.trim();

        if (!firstImageId) {
          setFailed(true);
          return;
        }

        const response = await fetch(`${baseURL}${imageFormat}${firstImageId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          setFailed(true);
          return;
        }

        const blob = await response.blob();

        if (!blob || blob.size === 0) {
          setFailed(true);
          return;
        }

        objectURL = URL.createObjectURL(blob);

        if (!cancelled) {
          setSrc(objectURL);
        }
      } catch (err) {
        console.error(err);

        if (!cancelled) {
          setFailed(true);
        }
      }
    }

    loadAvatarImage();

    return () => {
      cancelled = true;

      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [imageId, imageFormat, token]);

  if (src && !failed) {
    return (
      <Avatar
        src={src}
        alt={fallbackText}
        sx={{
          width: 50,
          height: 50
        }}
      />
    );
  }

  return (
    <Avatar
      {...stringAvatar(fallbackText || "?")}
      sx={{
        ...stringAvatar(fallbackText || "?").sx,
        width: 50,
        height: 50
      }}
    />
  );
});

function QueryCell({ row, item, primaryKey, token }) {

  const [value, setValue] = React.useState("");

  React.useEffect(() => {

    async function loadQueryValue() {

      try {

        const primaryValue = row?.[primaryKey];

        if (!primaryValue || !item?.sDataSource || !item?.sColumnID) {
          setValue("");
          return;
        }

        const response = await fetch(
          `${baseURL}${item.sDataSource}${primaryValue}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          setValue("");
          return;
        }

        const result = await response.json();

        const record = result?.data?.records?.[0];

        if (!record) {
          setValue("");
          return;
        }

        const displayValue = record?.[item.sColumnID] || "";

        setValue(
          sprintf(item.sFormat || "%s", displayValue)
        );

      } catch (err) {
        console.error(err);
        setValue("");
      }
    }

    loadQueryValue();

  }, [row, item, primaryKey, token]);

  if (!value) {
    return null;
  }

  return (
    <Typography>
      {value}
    </Typography>
  );
}

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography variant="caption" component="div" color="text.primary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

function CustomLoadingOverlay() {
  const [progress, setProgress] = React.useState(10);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prevProgress => (prevProgress >= 100 ? 0 : prevProgress + 10));
    }, 800);
    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <StyledGridOverlay>
      <CircularProgressWithLabel value={progress} />
      <Box sx={{ mt: 2 }}>Loading rows…</Box>
    </StyledGridOverlay>
  );
}

function CustomPagination({ apiRef, totalRows, pageSizeLimit }) {
  const safeTotal = Number(totalRows) || 0;
  const safeLimit = Number(pageSizeLimit) || 25;

  const handleFirstPage = () => {
    apiRef.current.setPage(0);
  };

  const handleLastPage = () => {
    const pageCount = Math.ceil(safeTotal / safeLimit);
    apiRef.current.setPage(Math.max(pageCount - 1, 0));
  };

  const currentPage = apiRef.current?.state?.pagination?.paginationModel?.page ?? 0;
  const pageCount = Math.ceil(safeTotal / safeLimit);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        onClick={handleFirstPage}
        disabled={currentPage === 0}
        aria-label="first page"
        size="small"
        sx={{
          '&:focus': { outline: 'none' },
          '&:not(.Mui-disabled)': {
            color: '#000',
          },
          '&.Mui-disabled': {
            color: '#9e9e9e', // MUI gray
          },
        }}
      >
        <FirstPageIcon />
      </IconButton>
      <GridPagination />
      <IconButton
        onClick={handleLastPage}
        disabled={currentPage >= pageCount - 1}
        aria-label="last page"
        size="small"
        sx={{
          '&:focus': { outline: 'none' },
          '&:not(.Mui-disabled)': {
            color: '#000',
          },
          '&.Mui-disabled': {
            color: '#9e9e9e',
          },
        }}
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

export default function ColumnTypesGrid({
  data,
  formdata,
  setselectedRowsDataGrid,
  format,
  setFreeFormTabbleEditMainrecord,
  dataGridModalOpen,
  setDataGridModalOpen,
  refreshData,
  setRefreshData,
  bulkActionLoader,
  setBulkActionLoader
}) {

  const { 
    setModalActionTypeAndID, token, handleClickOpen, setModalEditData, textValue, setFetchDataHandleDataGrid, setEditApi,
    mainFormData, setOverLaySplit, setOverLayDataFetchApi, setMultiDocumentSelectData, dataGridOptions, setDataGridOptions,
    setTextValue, setBulkData, registerBulkOperation, reportPrintUpdate, globalvariables
  } = Global_Data();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const gridIdentity = `${pathname}|${formdata?.component?.sName || "DATAGRID"}|${formdata?.data?.sDataSource || ""}`;
  const currentGridIdentityRef = React.useRef(gridIdentity);
  const previousGridIdentityRef = React.useRef(gridIdentity);
  const gridRequestSequenceRef = React.useRef(0);
  const gridAbortControllerRef = React.useRef(null);
  const rowVerificationSequenceRef = React.useRef(0);
  const gridRefreshOwnerRef = React.useRef(`datagrid-${Date.now()}-${Math.random()}`);
  const gridMountedRef = React.useRef(true);
  currentGridIdentityRef.current = gridIdentity;

  useEffect(() => {
    if (previousGridIdentityRef.current === gridIdentity) return;

    gridAbortControllerRef.current?.abort();
    gridAbortControllerRef.current = null;
    gridRequestSequenceRef.current += 1;
    rowVerificationSequenceRef.current += 1;
    setProgress(false);
    previousGridIdentityRef.current = gridIdentity;
  }, [gridIdentity]);

  useEffect(() => () => {
    gridMountedRef.current = false;
    gridAbortControllerRef.current?.abort();
    gridAbortControllerRef.current = null;
    gridRequestSequenceRef.current += 1;
    rowVerificationSequenceRef.current += 1;
  }, []);

  const beginGridRequest = (requestUri, reason) => {
    gridAbortControllerRef.current?.abort();

    const controller = new AbortController();
    gridAbortControllerRef.current = controller;
    const requestId = ++gridRequestSequenceRef.current;
    setProgress(true);
    const verificationGeneration = beginRowVerification();
    const startedAt = Date.now();
    const requestIdentity = gridIdentity;

    console.info("[DataGridRequest]", {
      phase: "start",
      requestId,
      reason,
      gridIdentity: requestIdentity,
      pathname,
      requestUri,
      startedAt: new Date(startedAt).toISOString(),
      authorization: token ? "present" : "missing"
    });

    return {
      requestId,
      startedAt,
      requestIdentity,
      requestUri,
      reason,
      controller,
      verificationGeneration
    };
  };

  const finishGridRequest = request => {
    if (!request || !isCurrentGridRequest(request)) return;

    if (gridAbortControllerRef.current === request.controller) {
      gridAbortControllerRef.current = null;
    }
    setProgress(false);
  };

  const isCurrentGridRequest = request =>
    gridMountedRef.current &&
    request?.requestId === gridRequestSequenceRef.current &&
    request?.requestIdentity === currentGridIdentityRef.current;

  const logGridRequestCompletion = (request, response, error, applied) => {
    const completedAt = Date.now();
    console.info("[DataGridRequest]", {
      phase: "complete",
      requestId: request?.requestId,
      reason: request?.reason,
      gridIdentity: request?.requestIdentity,
      currentGridIdentity: currentGridIdentityRef.current,
      requestUri: request?.requestUri,
      startedAt: new Date(request?.startedAt).toISOString(),
      completedAt: new Date(completedAt).toISOString(),
      durationMs: completedAt - request?.startedAt,
      authorization: token ? "present" : "missing",
      httpStatus: response?.status || error?.response?.status || null,
      applicationStatus: response?.data?.metadata?.status || error?.response?.data?.metadata?.status || null,
      recordCount: response?.data?.data?.records?.length,
      applied,
      responseBody: response?.data || error?.response?.data || null
    });
  };
  const apiRef = useGridApiRef();
  const ignoreNextFilterChange = React.useRef(false);
  const restoredSelectFilter = resolveRestoredDataGridFilter(
    formdata?.component?.sName,
    dataGridOptions
  );
  const filterExplicitlySelected = React.useRef(Boolean(restoredSelectFilter));

  const [rows, setRows] = React.useState([]);
  const [progress, setProgress] = React.useState(false);
  const [pageSizeLimit, setPageSizeLimit] = React.useState(formdata?.component?.pagination?.limit);
  const [totalRows, setTotalRows] = React.useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  const [searchedRows, setSearchedRows] = useState([]);
  const [searchedText, setSearchedText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [filter1, setFilter1] = React.useState([]);
  const [filter2, setFilter2] = React.useState([]);
  const [filter3, setFilter3] = React.useState([]);
  const [filter4, setFilter4] = React.useState([]);
  const [selectFilter, setSelectFilter] = React.useState(restoredSelectFilter);
  const [selectFilter2, setSelectFilter2] = React.useState("");
  const [statusFieldName, setStatusFieldName] = React.useState();
  const [printCounter, setPrintCounter] = React.useState([]);
  const [printDisabledStatus, setPrintDisabledStatus] = React.useState([]);
  const [editDisabledStatus, setEditDisabledStatus] = React.useState([]);
  const [viewDisabledStatus, setViewDisabledStatus] = React.useState([]);
  const [cancelDisabledStatus, setCancelDisabledStatus] = React.useState([]);
  const [deleteDisabledStatus, setDeleteDisabledStatus] = React.useState([]);
  const [custom1DisabledStatus, setCustom1DisabledStatus] = React.useState([]);
  const [custom2DisabledStatus, setCustom2DisabledStatus] = React.useState([]);
  const [custom3DisabledStatus, setCustom3DisabledStatus] = React.useState([]);
  const [selectedBulkAction, setSelectedBulkAction] = React.useState("");
  const [bulkActions, setBulkActions] = React.useState([]);
  const [activityAccess, setActivityAccess] = React.useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState("");
  const [selectedBulkPKValues, setSelectedBulkPKValues] = useState("");
  const [selectedBulkRows, setSelectedBulkRows] = useState([]);
  const [selectedDataGridRowIds, setSelectedDataGridRowIds] = useState([]);
  const bulkActionWasRunningRef = React.useRef(false);
  const ignoreProgrammaticSelectionClearRef = React.useRef(false);

  const dataGridPrimaryKey =
    mainFormData?.form?.sPrimaryKey || data?.form?.sPrimaryKey;

  const getRowVerificationId = row =>
    resolveDataGridRowId(row, dataGridPrimaryKey, row?.id);

  const prepareDataGridRows = (records, fallbackScope) =>
    assignStableDataGridRowIds(records, dataGridPrimaryKey, fallbackScope);

  const clearBulkSelection = React.useCallback(() => {
    ignoreProgrammaticSelectionClearRef.current = true;
    setRowSelectionModel("");
    setSelectedBulkPKValues("");
    setSelectedBulkRows([]);
    setSelectedDataGridRowIds([]);
    setMultiDocumentSelectData([]);
    setselectedRowsDataGrid([]);
    setSelectedBulkAction("");
    apiRef.current?.setRowSelectionModel([]);
  }, [apiRef, setMultiDocumentSelectData, setselectedRowsDataGrid]);

  useEffect(() => {
    if (bulkActionLoader) {
      bulkActionWasRunningRef.current = true;
      return;
    }

    if (bulkActionWasRunningRef.current) {
      bulkActionWasRunningRef.current = false;
      clearBulkSelection();
    }
  }, [bulkActionLoader, clearBulkSelection]);

  useEffect(() => {
    if (selectedDataGridRowIds.length === 0) {
      ignoreProgrammaticSelectionClearRef.current = false;
    }
  }, [selectedDataGridRowIds]);

  const isVerifyConfigured = verifyValue =>
    String(verifyValue ?? "").trim() !== "";

  const resetActionVerificationStatuses = () => {
    const retainSelected = statuses => retainSelectedVerificationStatuses(
      statuses,
      selectedBulkRows,
      dataGridPrimaryKey
    );

    setPrintDisabledStatus(retainSelected);
    setEditDisabledStatus(retainSelected);
    setViewDisabledStatus(retainSelected);
    setCancelDisabledStatus(retainSelected);
    setDeleteDisabledStatus(retainSelected);
    setCustom1DisabledStatus(retainSelected);
    setCustom2DisabledStatus(retainSelected);
    setCustom3DisabledStatus(retainSelected);
  };

  const beginRowVerification = () => {
    const generation = ++rowVerificationSequenceRef.current;
    resetActionVerificationStatuses();
    return generation;
  };

  const isCurrentRowVerification = generation =>
    generation == null ||
    (gridMountedRef.current && generation === rowVerificationSequenceRef.current);

  const updateRowVerification = (generation, setStatus, update) => {
    if (isCurrentRowVerification(generation)) {
      setStatus(update);
    }
  };

  const fetchRowVerification = (uri, requestOptions, requestCache, actionKey) => {
    const request = () => fetch(`${baseURL}${uri}`, requestOptions).then(response => response.json());

    if (!requestCache) {
      return request();
    }

    const cacheKey = `${actionKey}:${uri}`;
    if (!requestCache.has(cacheKey)) {
      requestCache.set(cacheKey, request());
    }

    return requestCache.get(cacheKey);
  };

  const RowCheckbox = (props) => {
    const inputRef = React.useCallback((node) => {
      if (!node) return;
      const rowEl = node.closest('[data-id]');
      const rowId = rowEl?.getAttribute("data-id");
      const rowIndex = rows?.findIndex((rowItem) => rowItem.id == rowId);
      if (rowId && rowIndex != -1) {
        node.id = `${formdata?.component?.sName}-checkbox-${rows?.[rowIndex]?.[data?.form?.sPrimaryKey]}`;
      }
    }, []);

    return <Checkbox {...props} inputRef={inputRef} />;
  };

  function replaceUriParams(data, uri) {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const regex = new RegExp(`{${key}}`, "g");
        uri = uri.replace(regex, data[key]);
      }
    }
    return uri;
  }

  const shouldUseGridData = formdata?.component?.sName === dataGridOptions?.dataGrid;
  const latestStateRef = React.useRef({});

  useEffect(() => {
    latestStateRef.current = {
      dataGrid: formdata?.component?.sName, pageSizeLimit, totalRows, currentPage, searchCurrentPage, searchedRows, 
      searchedText, filter1, filter2, filter3, filter4, selectFilter, rows, isSearching
    };
  }, [
    formdata?.component?.sName, pageSizeLimit, totalRows, currentPage, searchCurrentPage, searchedRows, searchedText, 
    filter1, filter2, filter3, filter4, selectFilter, rows, isSearching
  ]);

  useEffect(() => {
    if(refreshData){
      if(isSearching){
        fetchSearchedQueryData(searchedText);
      } else {
        fetchDataHandleDataGrid();
      }
      setRefreshData(false);
    }
  }, [refreshData])

  const fetchBulkActions = async () => {
     const sourceURL = formdata?.data?.sBulkAction?.sActions?.sSource;
    const requestOptions = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    if(sourceURL){
      const response = await fetch(`${baseURL}${sourceURL}`,
        requestOptions
      );
      const resultData = await response.json();
      setBulkActions(resultData?.data?.records);
    }
  }

  useEffect(() => {
   fetchBulkActions();
  }, [])

  useEffect(() => {
    if(!shouldUseGridData){
      const dataGridName = dataGridOptions?.dataGrid;
      setDataGridOptions(() => ({ dataGrid: dataGridName }));
    } else {
      if(formdata?.component?.sName === dataGridOptions?.dataGrid){
        if(Object.keys(dataGridOptions)?.length > 1){
          // A restored filter is still an explicit user selection. Preserve
          // that fact so the first request after returning from Edit uses the
          // filtered server endpoint instead of fetching page 1 unfiltered
          // and filtering only those 25 rows in the browser.
          filterExplicitlySelected.current = Boolean(dataGridOptions?.selectFilter);
          setSelectFilter(dataGridOptions?.selectFilter);
          setCurrentPage(dataGridOptions?.currentPage);
          setPageSizeLimit(dataGridOptions?.pageSizeLimit);
          setIsSearching(dataGridOptions?.isSearching);
          setSearchCurrentPage(dataGridOptions?.searchCurrentPage);
          setSearchedText(dataGridOptions?.searchedText);
          const dataGridName = dataGridOptions?.dataGrid;
          setDataGridOptions(() => ({ dataGrid: dataGridName }));
        }
      }
    }
  }, [shouldUseGridData, formdata?.component?.sName]);

  useEffect(() => { 
    return () => {
      setDataGridOptions(latestStateRef.current);
    };
  }, []);

  useEffect(() => {
    const fieldName = formdata?.fixcolumns?.filter((item) => item.sHeader == "Status")?.[0]?.sColumnID;
    setStatusFieldName(fieldName);
  }, [formdata]);

const upsertById = (prev, id, patch) => {
  const idx = prev.findIndex(item => item.id === id);
  if (idx !== -1) {
    const next = [...prev];
    next[idx] = { ...next[idx], ...patch };
    return next;
  }
  return [...prev, { id, ...patch }];
};

useEffect(() => {
  if (!reportPrintUpdate || reportPrintUpdate.count == null) return;

  const configuredPrintRoutes = [
    formdata?.data?.sAction?.actionPrint,
    formdata?.data?.sAction?.actionPrintVerify,
    ...rows.flatMap(row => [
      resolveRowAction(formdata?.data?.sAction?.actionPrint, row),
      resolveRowAction(formdata?.data?.sAction?.actionPrintVerify, row)
    ])
  ].filter(Boolean).join("|");

  if (
    reportPrintUpdate.reportId != null &&
    configuredPrintRoutes &&
    !configuredPrintRoutes.includes(String(reportPrintUpdate.reportId))
  ) {
    return;
  }

  const affectedIds = [
    reportPrintUpdate.recordId,
    reportPrintUpdate.transactionId
  ].filter(id => id !== undefined && id !== null && id !== "");

  if (affectedIds.length === 0) return;

  setPrintCounter(prev => {
    const next = [...prev];

    [...new Set(affectedIds)].forEach(id => {
      const index = next.findIndex(item => item.id == id);
      if (index >= 0) {
        next[index] = { ...next[index], count: reportPrintUpdate.count };
      } else {
        next.push({ id, count: reportPrintUpdate.count });
      }
    });

    return next;
  });
}, [reportPrintUpdate]);

const handlePrintVerify = async ({ field, verificationGeneration, requestCache }) => {
  // Skip verify when parent form data is not loaded or in ADD mode
  const fa = data?.form?.sFormAction;
  if (!data?.form || fa === "ADD" || fa === "add") return;
  try {
    const action = formdata.data.sAction.actionPrintVerify;
    const uri = resolveRowAction(action, field);
    const requestOptions = { headers: { Authorization: `Bearer ${token}` } };

    if (!uri.includes("{") && !uri.includes("}") && !uri.includes("[") && !uri.includes("]")) {
      const data = await fetchRowVerification(uri, requestOptions, requestCache, "print");

      const verificationId = getRowVerificationId(field);
      updateRowVerification(verificationGeneration, setPrintCounter,
        prev => upsertById(prev, verificationId, { count: data?.data?.PrintCount }));

      const allowed = data?.data?.Access === "Yes";
      updateRowVerification(verificationGeneration, setPrintDisabledStatus,
        prev => upsertById(prev, verificationId, { status: !allowed }));
    } else {
      // invalid/templated URI → disable print
      updateRowVerification(verificationGeneration, setPrintDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: true }));
    }
  } catch (err) {
    console.error("Error in handlePrintVerify:", err);
    updateRowVerification(verificationGeneration, setPrintDisabledStatus,
      prev => upsertById(prev, getRowVerificationId(field), { status: true }));
  }
};

const handleEditVerify = async ({ field, verificationGeneration, requestCache }) => {
  // Skip verify when parent form data is not loaded or in ADD mode
  const fa = data?.form?.sFormAction;
  if (!data?.form || fa === "ADD" || fa === "add") return;
  try {
    const action = formdata.data.sAction.actionEditVerify;
    const uri = resolveRowAction(action, field);
    const requestOptions = { headers: { Authorization: `Bearer ${token}` } };

    if (!uri.includes("{") && !uri.includes("}") && !uri.includes("[") && !uri.includes("]")) {
      const dataEdit = await fetchRowVerification(uri, requestOptions, requestCache, "edit");

      const allowed = dataEdit?.data?.Access === "Yes";
      updateRowVerification(verificationGeneration, setEditDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: !allowed }));
    } else {
      // if URI is templated, safest is to disable edit
      updateRowVerification(verificationGeneration, setEditDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: true }));
    }
  } catch (err) {
    console.error("Error in handleEditVerify:", err);
    updateRowVerification(verificationGeneration, setEditDisabledStatus,
      prev => upsertById(prev, getRowVerificationId(field), { status: true }));
  }
};

const handleCancelVerify = async ({ field, verificationGeneration, requestCache }) => {
  // Skip verify when parent form data is not loaded or in ADD mode
  const fa = data?.form?.sFormAction;
  if (!data?.form || fa === "ADD" || fa === "add") return;
  try {
    const action = formdata.data.sAction.actionCancelVerify;
    const uri = resolveRowAction(action, field);
    console.log("eklwnjkewnewq", field);
    const requestOptions = { headers: { Authorization: `Bearer ${token}` } };

    if (!uri.includes("{") && !uri.includes("}") && !uri.includes("[") && !uri.includes("]")) {
      const dataCancel = await fetchRowVerification(uri, requestOptions, requestCache, "cancel");

      const allowed = dataCancel?.data?.Access === "Yes";
      updateRowVerification(verificationGeneration, setCancelDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: !allowed }));
    } else {
      updateRowVerification(verificationGeneration, setCancelDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: true }));
    }
  } catch (err) {
    console.error("Error in handleCancelVerify:", err);
    updateRowVerification(verificationGeneration, setCancelDisabledStatus,
      prev => upsertById(prev, getRowVerificationId(field), { status: true }));
  }
};

const handleDeleteVerify = async ({ field, verificationGeneration, requestCache }) => {
  // Skip verify when parent form data is not loaded or in ADD mode
  const fa = data?.form?.sFormAction;
  if (!data?.form || fa === "ADD" || fa === "add") return;
  try {
    const action = formdata.data.sAction.actionDeleteVerify;
    const uri = resolveRowAction(action, field);
    const requestOptions = { headers: { Authorization: `Bearer ${token}` } };

    if (!uri.includes("{") && !uri.includes("}") && !uri.includes("[") && !uri.includes("]")) {
      const dataDelete = await fetchRowVerification(uri, requestOptions, requestCache, "delete");

      const allowed = dataDelete?.data?.Access === "Yes";
      updateRowVerification(verificationGeneration, setDeleteDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: !allowed }));
    } else {
      updateRowVerification(verificationGeneration, setDeleteDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: true }));
    }
  } catch (err) {
    console.error("Error in handleDeleteVerify:", err);
    updateRowVerification(verificationGeneration, setDeleteDisabledStatus,
      prev => upsertById(prev, getRowVerificationId(field), { status: true }));
  }
};

const handleViewVerify = async ({ field, verificationGeneration, requestCache }) => {
  // Skip verify when parent form data is not loaded or in ADD mode
  const fa = data?.form?.sFormAction;
  if (!data?.form || fa === "ADD" || fa === "add") return;
  try {
    const action = formdata.data.sAction.actionViewVerify;
    const uri = resolveRowAction(action, field);
    const requestOptions = { headers: { Authorization: `Bearer ${token}` } };

    if (!uri.includes("{") && !uri.includes("}") && !uri.includes("[") && !uri.includes("]")) {
      const dataView = await fetchRowVerification(uri, requestOptions, requestCache, "view");

      const allowed = dataView?.data?.Access === "Yes";
      updateRowVerification(verificationGeneration, setViewDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: !allowed }));
    } else {
      updateRowVerification(verificationGeneration, setViewDisabledStatus,
        prev => upsertById(prev, getRowVerificationId(field), { status: true }));
    }
  } catch (err) {
    console.error("Error in handleViewVerify:", err);
    updateRowVerification(verificationGeneration, setViewDisabledStatus,
      prev => upsertById(prev, getRowVerificationId(field), { status: true }));
  }
};

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;

  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const parseCustomVerifyConfig = (verifyConfig = "") => {
  const parts = String(verifyConfig).split(":");

  return {
    responsePath: parts[0] || "",
    enabledValue: parts[1] || "",
    disabledValue: parts[2] || "",
    api: parts.slice(3).join(":") || "",
  };
};

const handleCustomVerify = async ({
  field,
  actionVerify,
  setDisabledStatus,
  verificationGeneration,
  requestCache,
}) => {
  const fa = data?.form?.sFormAction;

  if (!data?.form || fa === "ADD" || fa === "add") {
    return;
  }

  const pkValue = getRowVerificationId(field);

  const {
    responsePath,
    enabledValue,
    disabledValue,
    api,
  } = parseCustomVerifyConfig(actionVerify);

  try {
    const uri = resolveRowAction(api, field);

    if (
      !responsePath ||
      !enabledValue ||
      !api ||
      uri.includes("{") ||
      uri.includes("}") ||
      uri.includes("[") ||
      uri.includes("]")
    ) {
      updateRowVerification(verificationGeneration, setDisabledStatus,
        prev => upsertById(prev, pkValue, { status: true }));
      return;
    }

    const result = await fetchRowVerification(uri, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }, requestCache, `custom:${actionVerify}`);
    const responseValue = getValueByPath(result, responsePath);

    const enabled =
      String(responseValue) === String(enabledValue);

    const disabled =
      disabledValue !== ""
        ? String(responseValue) === String(disabledValue)
        : !enabled;

    updateRowVerification(verificationGeneration, setDisabledStatus,
      prev => upsertById(prev, pkValue, {
        status: disabled || !enabled,
      }));
  } catch (err) {
    console.error("Error in handleCustomVerify:", err);

    updateRowVerification(verificationGeneration, setDisabledStatus,
      prev => upsertById(prev, pkValue, { status: true }));
  }
};

  const verifyRowActions = (records, verificationGeneration) => {
    const formAction = data?.form?.sFormAction;
    const isAddMode =
      !data?.form || formAction === "ADD" || formAction === "add";

    if (isAddMode) return;

    const actions = formdata?.data?.sAction || {};
    const requestCache = new Map();
    const generation = verificationGeneration ?? beginRowVerification();

    records?.forEach(field => {
      if (isVerifyConfigured(actions.actionEditVerify)) {
        handleEditVerify({ field, verificationGeneration: generation, requestCache });
      }
      if (isVerifyConfigured(actions.actionViewVerify)) {
        handleViewVerify({ field, verificationGeneration: generation, requestCache });
      }
      if (isVerifyConfigured(actions.actionCancelVerify)) {
        handleCancelVerify({ field, verificationGeneration: generation, requestCache });
      }
      if (isVerifyConfigured(actions.actionDeleteVerify)) {
        handleDeleteVerify({ field, verificationGeneration: generation, requestCache });
      }
      if (isVerifyConfigured(actions.actionPrintVerify)) {
        handlePrintVerify({ field, verificationGeneration: generation, requestCache });
      }
      if (isVerifyConfigured(actions.actionCustom1Verify)) {
        handleCustomVerify({
          field,
          actionVerify: actions.actionCustom1Verify,
          setDisabledStatus: setCustom1DisabledStatus,
          verificationGeneration: generation,
          requestCache,
        });
      }
      if (isVerifyConfigured(actions.actionCustom2Verify)) {
        handleCustomVerify({
          field,
          actionVerify: actions.actionCustom2Verify,
          setDisabledStatus: setCustom2DisabledStatus,
          verificationGeneration: generation,
          requestCache,
        });
      }
      if (isVerifyConfigured(actions.actionCustom3Verify)) {
        handleCustomVerify({
          field,
          actionVerify: actions.actionCustom3Verify,
          setDisabledStatus: setCustom3DisabledStatus,
          verificationGeneration: generation,
          requestCache,
        });
      }
    });
  };


  const fetchDataHandleDataGrid = async () => {
    // Skip entirely if parent form data is not loaded (e.g., during route transition)
    if (!data?.form) return;
    let gridRequest;
    let response;
    try {
      if(formdata?.component?.pagination?.type == "server"){
        const uri = baseURL + replaceUriParams(
          textValue,
          (selectFilter && filterExplicitlySelected.current) ?
          selectFilter :
          (formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.selectFilter && filterExplicitlySelected.current) ?
          dataGridOptions?.selectFilter :
          formdata.data.sDataSource
        );
        let apiURI = "";
        if(uri.includes("?")){
          apiURI = `${uri}&page=${(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.currentPage) ? dataGridOptions?.currentPage : currentPage}&limit=${(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.pageSizeLimit) ? dataGridOptions?.pageSizeLimit : pageSizeLimit}`;
        } else {
          if(uri.endsWith("/")){
            apiURI = `${uri.slice(0, uri.length - 1)}?page=${(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.currentPage) ? dataGridOptions?.currentPage : currentPage}&limit=${(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.pageSizeLimit) ? dataGridOptions?.pageSizeLimit : pageSizeLimit}`;
          } else {
            apiURI = `${uri}?page=${(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.currentPage) ? dataGridOptions?.currentPage : currentPage}&limit=${(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.pageSizeLimit) ? dataGridOptions?.pageSizeLimit : pageSizeLimit}`;
          }
        }
        gridRequest = beginGridRequest(apiURI, "load");
        response = await axios.get(
          apiURI,
          {
            signal: gridRequest.controller.signal,
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      } else {    
        const apiURI = baseURL + replaceUriParams(textValue, formdata.data.sDataSource);
        gridRequest = beginGridRequest(apiURI, "load");
        response = await axios.get(
          apiURI,
          {
            signal: gridRequest.controller.signal,
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      if (!isCurrentGridRequest(gridRequest)) {
        logGridRequestCompletion(gridRequest, response, null, false);
        return;
      }

      logGridRequestCompletion(gridRequest, response, null, true);

      const responseRecords = response?.data?.data?.records;
      if (!Array.isArray(responseRecords)) {
        throw new Error("DataGrid response does not contain a records array.");
      }
      if(filter1?.[0]?.sValue && !selectFilter){
        setSelectFilter(filter1?.[0]?.sValue);
      }
      let items = prepareDataGridRows(
        responseRecords,
        `page_${currentPage}`
      );
      const dates = [];
      formdata?.fixcolumns?.forEach((clm) => {
        if(clm.sType === "date") dates.push(clm.sColumnID); 
      })
      if(items && items.length > 0){
        items.forEach((itm) => {
          for(let i = 0; i < dates.length; i++){
            if(!itm[dates[i]]){
              itm[dates[i]] = "";
            }
          }
        })
      }
      
      const columnIdAccess = [];
      if(formdata && formdata.fixcolumns && formdata.fixcolumns.length > 0){
        formdata.fixcolumns.forEach((clm) => {
          if(clm.sType === 'number'){
            for(let i = 0; i < items.length; i++){
              if(items[i][clm.sColumnID] == 0){
                items[i][clm.sColumnID] = String(items[i][clm.sColumnID]);
              }
            }
          }
          if(clm.sType === 'string' && clm.sColumnID?.includes(".")){
            columnIdAccess.push(clm.sColumnID);
          }
        })
      }

      items.forEach((field) => {
        columnIdAccess.forEach((item, index) => {
          const keys = columnIdAccess[index].split(".");
          field[item] = field[keys[0]][keys[1]];
        });
      })
      verifyRowActions(items, gridRequest.verificationGeneration);

      let fieldName = statusFieldName;
      if(!statusFieldName){
        fieldName = formdata?.fixcolumns?.filter((item) => item.sHeader == "Status")?.[0]?.sColumnID;
      }

      if(selectFilter){
        const filteredData = items.filter((row) => {
          if(selectFilter?.includes(fieldName)) {
            if(selectFilter?.includes(`${fieldName}=${row[fieldName]}`)){
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        });
        setRows(filteredData);
      } else if(formdata?.component?.sName === dataGridOptions?.dataGrid && dataGridOptions?.selectFilter) {
        const filteredData = items.filter((row) => {
          if(dataGridOptions?.selectFilter?.includes(fieldName)) {
            if(dataGridOptions?.selectFilter?.includes(`${fieldName}=${row[fieldName]}`)){
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        });
        setRows(filteredData);
      } else {
        setRows(items);
      }

      const initialTotal = response?.data?.data?.totalRows;
      setTotalRows(String(initialTotal != null ? initialTotal : items.length));
      if(formdata.data.sDataSource?.includes(mainFormData?.form?.sPrimaryTable)){
        localStorage.setItem('GridData', JSON.stringify(items));
      }      
      
      } 
      catch (error) {
      if (gridRequest && !isCurrentGridRequest(gridRequest)) {
        logGridRequestCompletion(gridRequest, response, error, false);
        return;
      }

      if (gridRequest) {
        logGridRequestCompletion(gridRequest, response, error, false);
      }
      if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
        return;
      }
      // Add 401 handling here
      if (error?.response?.status == "401") {
        localStorage.clear();
        navigate("/login");
        return;
      }
      console.error("[DataGridRequest] Request failed", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg || error?.message || "Unable to load grid data.",
        { domain: "datagrid", action: "load" }
      );
    } finally {
      if (!gridRequest) setProgress(false);
      finishGridRequest(gridRequest);
    }
  };
  //     catch (error) {
  //     setProgress(false);
  //     console.log(error);
  //   }
  // };

  useEffect(() => {
    if(!searchedText || searchedText == ""){
      setIsSearching(false);
      setSearchedRows([]);
    }
  }, [searchedText])

  function findObjects(data, keyName, value) {
    const index = data.findIndex(item => item[keyName] === value);
    if (index === -1) {
      return null;
    }
    return {
      preOBJ: data[index - 1] || null,
      cruntOBJ: data[index],
      nextOBJ: data[index + 1] || null
    };
  }

  useEffect(() => {
    const filters = ['filter1', 'filter2', 'filter3', 'filter4'];
    const setters = { filter1: setFilter1, filter2: setFilter2, filter3: setFilter3, filter4: setFilter4 };

    filters.forEach((filterKey) => {
      const filter = formdata?.data?.sFilters?.[filterKey];
      if (filter?.enabled) {
        const uri = `${baseURL}${replaceUriParams(textValue, filter.sSource)}`;
        axios.get(uri, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            setters[filterKey](res?.data?.data?.records);
            if (filterKey === 'filter2' && res?.data?.data?.records?.length > 0) {
              setSelectFilter2(prev => prev || res?.data?.data?.records[0]?.sValue || "");
            }
          })
          .catch(console.log);
      }
    });
  }, [formdata, pathname]);

  const handleFilter1 = initialURI => {
    filterExplicitlySelected.current = true;
    const filterdata = filter1?.filter(elm => elm?.sValue === initialURI);
    setSelectFilter(filterdata[0]?.sValue);
    let uri = replaceUriParams(textValue, initialURI);

    if (selectFilter2 && selectFilter2 !== "All") {
      uri = uri.includes("?") ? `${uri}&${selectFilter2}` : `${uri}?${selectFilter2}`;
    }

    if(formdata?.component?.pagination?.type == "server"){
      if(uri.includes("?")){
        uri = `${uri}&page=${currentPage}&limit=${pageSizeLimit}`;
      } else {
        uri = `${uri}?page=${currentPage}&limit=${pageSizeLimit}`;
      }
    }

    const requestUri = baseURL + uri;
    const gridRequest = beginGridRequest(requestUri, "filter-1");

    axios
      .get(requestUri, {
        signal: gridRequest.controller.signal,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then(response => {
        if (!isCurrentGridRequest(gridRequest)) {
          logGridRequestCompletion(gridRequest, response, null, false);
          return;
        }

        logGridRequestCompletion(gridRequest, response, null, true);
        if (!Array.isArray(response?.data?.data?.records)) {
          throw new Error("DataGrid filter response does not contain a records array.");
        }

        let items = prepareDataGridRows(
          response.data.data.records,
          `filter_1_page_${currentPage}`
        );
        const dates = [];
        formdata?.fixcolumns?.forEach((clm) => {
          if(clm.sType === "date") dates.push(clm.sColumnID); 
        })
        if(items && items.length > 0){
          items.forEach((itm) => {
            for(let i = 0; i < dates.length; i++){
              if(!itm[dates[i]]){
                itm[dates[i]] = "";
              }
            }
          })
        }
        const columnIdAccess = [];
        if(formdata && formdata.fixcolumns && formdata.fixcolumns.length > 0){
          formdata.fixcolumns.forEach((clm) => {
            if(clm.sType === 'number'){
              for(let i = 0; i < items.length; i++){
                if(items[i][clm.sColumnID] == 0){
                  items[i][clm.sColumnID] = String(items[i][clm.sColumnID]);
                }
              }
            }
            if(clm.sType === 'string' && clm.sColumnID?.includes(".")){
              columnIdAccess.push(clm.sColumnID);
            }
          })
        }

        items.forEach((field) => {
          columnIdAccess.forEach((item, index) => {
            const keys = columnIdAccess[index].split(".");
            field[item] = field[keys[0]][keys[1]];
          });
        })

        verifyRowActions(items, gridRequest.verificationGeneration);

        ignoreNextFilterChange.current = true;
        apiRef.current.setFilterModel({ items: [], quickFilterValues: [] });
        setIsSearching(false);
        setSearchedRows([]);
        const filteredTotal = response?.data?.data?.totalRows;
        setTotalRows(String(filteredTotal != null ? filteredTotal : items.length));
        setRows(items);
      })
      .catch(error => {
        if (!isCurrentGridRequest(gridRequest)) {
          logGridRequestCompletion(gridRequest, null, error, false);
          return;
        }

        logGridRequestCompletion(gridRequest, null, error, false);
        if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return;
        // Add 401 handling here
        if (error?.response?.status == "401") {
          localStorage.clear();
          navigate("/login");
          return;
        }
        console.error("[DataGridRequest] Filter request failed", error);
        CustomAlert(
          "error",
          error?.response?.data?.metadata?.msg || error?.message || "Unable to filter grid data.",
          { domain: "datagrid", action: "filter" }
        );
      })
      .finally(() => finishGridRequest(gridRequest));
  };
  //     .catch(error => console.log(error));
  // };

  const handleFilter2 = (filter2Value) => {
    setSelectFilter2(filter2Value);
    if (!selectFilter) return;

    let uri = replaceUriParams(textValue, selectFilter);

    if (filter2Value && filter2Value !== "All") {
      uri = uri.includes("?") ? `${uri}&${filter2Value}` : `${uri}?${filter2Value}`;
    }

    if (formdata?.component?.pagination?.type == "server") {
      uri = uri.includes("?") ? `${uri}&page=${currentPage}&limit=${pageSizeLimit}` : `${uri}?page=${currentPage}&limit=${pageSizeLimit}`;
    }

    const requestUri = baseURL + uri;
    const gridRequest = beginGridRequest(requestUri, "filter-2");

    axios
      .get(requestUri, {
        signal: gridRequest.controller.signal,
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        if (!isCurrentGridRequest(gridRequest)) {
          logGridRequestCompletion(gridRequest, response, null, false);
          return;
        }

        logGridRequestCompletion(gridRequest, response, null, true);
        if (!Array.isArray(response?.data?.data?.records)) {
          throw new Error("DataGrid filter response does not contain a records array.");
        }

        let items = prepareDataGridRows(
          response.data.data.records,
          `filter_2_page_${currentPage}`
        );
        const dates = [];
        formdata?.fixcolumns?.forEach((clm) => {
          if (clm.sType === "date") dates.push(clm.sColumnID);
        });
        if (items && items.length > 0) {
          items.forEach((itm) => {
            for (let i = 0; i < dates.length; i++) {
              if (!itm[dates[i]]) itm[dates[i]] = "";
            }
          });
        }
        verifyRowActions(items, gridRequest.verificationGeneration);
        ignoreNextFilterChange.current = true;
        apiRef.current.setFilterModel({ items: [], quickFilterValues: [] });
        setIsSearching(false);
        setSearchedRows([]);
        const filteredTotal2 = response?.data?.data?.totalRows;
        setTotalRows(String(filteredTotal2 != null ? filteredTotal2 : items.length));
        setRows(items);
      })
      .catch(error => {
        if (!isCurrentGridRequest(gridRequest)) {
          logGridRequestCompletion(gridRequest, null, error, false);
          return;
        }

        logGridRequestCompletion(gridRequest, null, error, false);
        if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return;
        if (error?.response?.status == "401") {
          localStorage.clear();
          navigate("/login");
          return;
        }
        console.error("[DataGridRequest] Filter request failed", error);
        CustomAlert(
          "error",
          error?.response?.data?.metadata?.msg || error?.message || "Unable to filter grid data.",
          { domain: "datagrid", action: "filter" }
        );
      })
      .finally(() => finishGridRequest(gridRequest));
  };

  const handleBuldActionChange = (value) => {
    setSelectedBulkAction(value);
  }

  const handleBulkActionClick = () => {
    const selectedBulkActionData = bulkActions.filter((actionItem) => actionItem?.sValue == selectedBulkAction)?.[0];
    const bulkConfiguration = formdata?.data?.sBulkAction?.sActions || {};
    const requiredActivity = resolveBulkRequiredActivity(selectedBulkActionData, bulkConfiguration);
    if (!requiredActivity || activityAccess[requiredActivity] !== "ALLOWED") {
      CustomAlert("error", "You do not have permission to perform this Bulk Action.");
      return;
    }
    if (!isBulkActionWorkflowAllowed(selectedBulkActionData)) {
      CustomAlert("error", "One or more selected records are not allowed for this Bulk Action.");
      return;
    }
    const operationId = createBulkOperationId();
    // Transaction Bulk actions use the shared SSE coordinator by default.
    // sProgressEnabled remains available for non-transaction Bulk endpoints
    // which explicitly implement the same progress-event contract.
    const progressEnabled = isBulkProgressEnabled(selectedBulkActionData, bulkConfiguration);

    const isEditAction = resolveBulkWorkflowVerification(
      selectedBulkActionData,
      bulkConfiguration
    ) === "edit";
    const sEditDialogName = formdata?.data?.sBulkAction?.sActions?.sEditDialogName;

    let urlValue, dialogName, bulkDataPayload;
    if (isEditAction && sEditDialogName) {
      const primaryKey = mainFormData?.form?.sPrimaryKey;
      urlValue = selectedBulkActionData?.sValue || "";
      dialogName = sEditDialogName?.split("?")?.[0];
      bulkDataPayload = {
        url: urlValue,
        action: selectedBulkActionData?.sChoice,
        pri: primaryKey,
        rec: selectedBulkPKValues,
        operationId: progressEnabled ? operationId : undefined,
        requiredActivity,
        progressEnabled
      };
    } else {
      urlValue = `${selectedBulkActionData?.sValue}${rowSelectionModel}`;
      dialogName = formdata?.data?.sBulkAction?.sActions?.sDialogName?.split("?")?.[0];
      bulkDataPayload = {
        url: urlValue,
        action: selectedBulkActionData?.sChoice,
        operationId: progressEnabled ? operationId : undefined,
        requiredActivity,
        progressEnabled
      };
    }

    if (progressEnabled) {
      registerBulkOperation?.(operationId, {
        module: data?.form?.sFormName,
        formType: data?.form?.sFormType,
        action: selectedBulkActionData?.sChoice
      });
    }

    const mode = {
      options: {
        mode: "DEFAULT",
        handler: "handleDialog",
        dialog: dialogName,
      }
    };
    setBulkActionLoader(true);
    setDataGridModalOpen(true);
    setBulkData(() => bulkDataPayload);
    handleClickOpen(mode);
  }

  const cancelItem = (id, params) => {
    const cancelAction = resolveRowAction(
      formdata.data.sAction.actionCancel,
      params?.row
    );
    
    const EditData = data?.details.filter((item, index) => item?.component?.sFormAction === "EDIT");
    setEditApi(`${EditData[0]?.component?.sFormSource}${params?.row[mainFormData.form.sPrimaryKey]}`);

    if (formdata.data.sAction.actionCancelType === "dialog") {
      setDataGridModalOpen(true);
      const mode = {
        options: {
          mode: "DEFAULT",
          handler: "handleDialog",
          dialog: cancelAction
        }
      };

      if(formdata?.data?.sAction?.altPrimaryKey && formdata?.data?.sAction?.altPrimaryKey != ""){
        setModalActionTypeAndID({
          type: "delete",
          id: params?.row[formdata?.data?.sAction?.altPrimaryKey],
          row: params?.row,
          PrimaryKey: params?.row[formdata?.data?.sAction?.altPrimaryKey]
        });
      } else {
        setModalActionTypeAndID({
          type: "delete",
          id: params?.row[mainFormData.form.sPrimaryKey],
          row: params?.row,
          PrimaryKey: params?.row[mainFormData.form.sPrimaryKey]
        });
      }

      handleClickOpen(mode);
    } else {
      if(formdata?.data?.sAction?.altPrimaryKey && formdata?.data?.sAction?.altPrimaryKey != ""){
        navigate(
          cancelAction + "?id=" + params?.row[formdata?.data?.sAction?.altPrimaryKey],
          { state: { id: params?.row[formdata?.data?.sAction?.altPrimaryKey] } }
        );
      } else {
        navigate(
          cancelAction + "?id=" + params?.row[mainFormData.form.sPrimaryKey],
          { state: { id: params?.row[mainFormData.form.sPrimaryKey] } }
        );
      }
    }
  };

  const editItem = (id, params) => {
    const editAction = resolveRowAction(
      formdata.data.sAction.actionEdit,
      params?.row
    );

    const EditData = data?.details.filter((item, index) => item?.component?.sFormAction === "EDIT");

    setEditApi(`${EditData[0]?.component?.sFormSource}${params?.row[mainFormData.form.sPrimaryKey]}`);

    if (formdata.data.sAction.actionEditType === "dialog") {
      setDataGridModalOpen(true);
      const mode = {
        options: {
          mode: "DEFAULT",
          handler: "handleDialog",
          dialog: editAction
        }
      };
      if(formdata?.data?.sAction?.altPrimaryKey && formdata?.data?.sAction?.altPrimaryKey != ""){
        setModalActionTypeAndID({ type: "edit", id: params?.row[formdata?.data?.sAction?.altPrimaryKey] });
      } else {
        setModalActionTypeAndID({ type: "edit", id: params?.row[mainFormData.form.sPrimaryKey] });
      }
      setModalEditData(params.row);

      handleClickOpen(mode);
    } else {
      if(formdata?.data?.sAction?.altPrimaryKey && formdata?.data?.sAction?.altPrimaryKey != ""){
        navigate(
          editAction + "?id=" + params?.row[formdata?.data?.sAction?.altPrimaryKey],
          { state: { id: params?.row[formdata?.data?.sAction?.altPrimaryKey] } }
        );
      } else {
        navigate(
          editAction + "?id=" + params?.row[mainFormData.form.sPrimaryKey],
          { state: { id: params?.row[mainFormData.form.sPrimaryKey] } }
        );
      }
    }
  };

  const viewItem = (id, params) => {
    const viewAction = resolveRowAction(
      formdata.data.sAction.actionView,
      params?.row
    );
    if (formdata.data.sAction.actionViewType === "dialog") {
      const mode = {
        options: {
          mode: "DEFAULT",
          handler: "handleDialog",
          dialog: viewAction
        }
      };
      handleClickOpen(mode);
    } else if (formdata.data.sAction.actionViewType === "overlay-url") {
      
      let keyName = mainFormData.form.sPrimaryKey;
      let value = params?.row?.[data.form.sPrimaryKey];
      const result = findObjects(rows, keyName, value);
     
      if (id && viewAction) {
        setOverLaySplit(true);
        setOverLayDataFetchApi(
          viewAction +
            `?id=${id}&next=${result?.nextOBJ?.[keyName] || null}&pre=${
              result?.preOBJ?.[keyName] || null
            }`
        );
        window.history.pushState(
          {},
          "",
          `?id=${id}&next=${result?.nextOBJ?.[keyName] || null}&pre=${
            result?.preOBJ?.[keyName] || null
          }`
        );
      }
    } else if(formdata.data.sAction.actionViewType === "url") {
      setOverLaySplit(false);
      let keyName = mainFormData.form.sPrimaryKey;
      let value = params?.row?.[mainFormData.form.sPrimaryKey];
      const result = findObjects(rows, keyName, value);
      if (id && viewAction) {
        navigate(
          viewAction +`?id=${id}`
        );
      }
    } else {
      let keyName = mainFormData.form.sPrimaryKey;
      let value = params?.row?.[mainFormData.form.sPrimaryKey];
      const result = findObjects(rows, keyName, value);
      if (id && viewAction) {
        navigate(
          viewAction +`?id=${id}`
        );
      }
    }
  };
  
  function resolveRowAction(actionTemplate, row) {
    return resolveDataGridRowAction(
      actionTemplate,
      row,
      formdata?.data?.sVariables,
      globalvariables
    );
  }

  const printItem = async (params) => {
    const printAction = resolveRowAction(
      formdata.data.sAction.actionPrint,
      params?.row
    );
    if (formdata.data.sAction.actionPrintType === "dialog") {
      const mode = {
        options: {
          mode: "DEFAULT",
          handler: "handleDialog",
          dialog: printAction
        }
      };
      handleClickOpen(mode);
    } else if(formdata.data.sAction.actionPrintType === "download") {
      const uri = printAction;
      const requestOptions = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const response = await fetch(`${baseURL}${uri}`,
        requestOptions
      );
      const blob = await response.blob();
      const objectURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectURL;
      link.download = 'document.pdf';
      link.click();
      URL.revokeObjectURL(objectURL);
    } else if(formdata.data.sAction.actionPrintType === "print") {
      const uri = printAction;
      const requestOptions = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const response = await fetch(`${baseURL}${uri}`,
        requestOptions
      );
      const blob = await response.blob();
      const objectURL = URL.createObjectURL(blob);
      const windowFeatures = 'width=1300,height=700,left=100,top=100';
      window.open(objectURL, '_blank', windowFeatures);
    } else {
      navigate(printAction);
    }
    handlePrintVerify({ field: params?.row })
  };

  const deleteItem = params => {
    const deleteAction = resolveRowAction(
      formdata.data.sAction.actionDelete,
      params?.row
    );

    if (formdata.data.sAction.actionDeleteType === "dialog") {
      setDataGridModalOpen(true);
      const mode = {
        options: {
          mode: "DEFAULT",
          handler: "handleDialog",
          dialog: deleteAction
        }
      };

      if(formdata?.data?.sAction?.altPrimaryKey && formdata?.data?.sAction?.altPrimaryKey != ""){
        setModalActionTypeAndID({
          type: "delete",
          id: params?.row[formdata?.data?.sAction?.altPrimaryKey],
          row: params?.row,
          PrimaryKey: params?.row[formdata?.data?.sAction?.altPrimaryKey]
        });
      } else {
        setModalActionTypeAndID({
          type: "delete",
          id: params?.row[mainFormData.form.sPrimaryKey],
          row: params?.row,
          PrimaryKey: params?.row[mainFormData.form.sPrimaryKey]
        });
      }

      handleClickOpen(mode);
    }
  };

  const customItem = (params, actionType, actionDialog, actionTypeKey) => {
    const customAction = resolveRowAction(actionDialog, params?.row);
    if (actionType === "dialog") {
      setDataGridModalOpen(true);
      const mode = {
        options: {
          mode: "DEFAULT",
          handler: "handleDialog",
          dialog: customAction
        }
      };
      setModalActionTypeAndID({
        type: actionTypeKey,
        id: params?.row[mainFormData.form.sPrimaryKey],
        row: params?.row,
        PrimaryKey: params?.row[mainFormData.form.sPrimaryKey]
      });
      setModalEditData(params.row);
      handleClickOpen(mode);
    }
  };

  const [isViewRestricted, setViewRestricted] = useState(false);
  const [isDELETERestricted, setDELETERestricted] = useState(false);
  const [isEDITRestricted, setEDITRestricted] = useState(false);
  const [isCANCELRestricted, setCANCELRestricted] = useState(false);
  const [isPRINTRestricted, setPRINTRestricted] = useState(false);
  const permissionModule = data?.form?.sFormName || "";
  const permissionFormAction = data?.form?.sFormAction || "";
  //const [userImages, setUserImages] = useState();

  function fetchActivity(activity) {
    // Skip when parent form data is not loaded or in ADD mode
    if (!permissionModule) return;
    setActivityAccess(previous => ({ ...previous, [activity]: "LOADING" }));
    const urlCapture =
      serverAddress +
      `/form/isallowed/transaction?module=${permissionModule}&activity=${activity}`;
    axios
      .get(urlCapture, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then(result => {
        const allowed = String(result?.data?.data?.Access || "").toUpperCase() === "YES";
        setActivityAccess(previous => ({
          ...previous,
          [activity]: allowed ? "ALLOWED" : "DENIED"
        }));
        if (result.data.data.Access == "No") {
          if (activity == "VIEW") {
            setViewRestricted(true);
          } else if (activity == "EDIT") {
            setEDITRestricted(true);
          } else if (activity == "DELETE") {
            setDELETERestricted(true);
          } else if (activity == "PRINT") {
            setPRINTRestricted(true);
          } else if (activity == "TXCANCEL") {
            setCANCELRestricted(true);
          }
        }
      })
      .catch(error => {
        setActivityAccess(previous => ({ ...previous, [activity]: "DENIED" }));
        console.error(error, "error456");
      });
  }

  function stringToColor(string) {
    let hash = 0;
    let i;
  
    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
  
    let color = '#';
  
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */
  
    return color;
  }
  
  function stringAvatar(name) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: name.split(' ').length <= 1 ? 
                `${name.split(' ')[0][0]}` :
                `${name.split(' ')[0][0]}${name.split(' ')[1]?.[0]}`,
    };
  }

  useEffect(() => {
    // Skip permission checks when parent form data is not loaded or in ADD mode
    if (!permissionModule || permissionFormAction.toUpperCase() === "ADD") return;
    setViewRestricted(false);
    setEDITRestricted(false);
    setDELETERestricted(false);
    setCANCELRestricted(false);
    setPRINTRestricted(false);
    fetchActivity("ADD");
    fetchActivity("EDIT");
    fetchActivity("DELETE");
    fetchActivity("VIEW");
    fetchActivity("PRINT");
    fetchActivity("TXCANCEL");
    return () => {
      setViewRestricted(false);
      setEDITRestricted(false);
      setDELETERestricted(false);
      setCANCELRestricted(false);
      setPRINTRestricted(false);
    };
  }, [permissionModule, permissionFormAction, token]);

  const formatDate = (date, format) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    let formattedDate = (date && !isNaN(new Date(date).getTime())) 
    ? new Intl.DateTimeFormat("en-US", options)?.format(new Date(date)) 
    : "";
    const [month, day, year] = formattedDate.split("/");

    switch (format) {
      case "dd-mm-yyyy":
        return `${day}-${month}-${year}`;
      case "mm-dd-yyyy":
        return `${month}-${day}-${year}`;
      case "yyyy-mm-dd":
        return `${year}-${month}-${day}`;
      default:
        return formattedDate;
    }
  };

  // async function fetchUserAvatarImage(reqFormat, userId) {
  //   const findInd = rows?.findIndex((item) => item?.sUserID == userId);
  //   if(findInd != -1){
  //     if(rows[findInd]?.sUserCode && rows[findInd]?.sUserCode?.sImageID) {
  //       const response = await fetch(
  //         baseURL + reqFormat + rows[findInd]?.sUserCode?.sImageID,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             responseType: 'blob'
  //           }
  //         }
  //       );
  //       const blob = await response.blob();
  //       const objectURL = URL.createObjectURL(blob);
  //       return objectURL;
  //     }
  //   }
  // }

  // useEffect(() => {
  //   if(!userImages && rows && rows.length > 0 && formdata){
  //     const imgIndex = formdata?.fixcolumns?.findIndex((item) => item?.sType == "image");
  //     if(imgIndex != -1){
  //       const imageList = [];
  //       rows?.forEach(async (row) => {
  //         const resImage = await fetchUserAvatarImage(formdata?.fixcolumns[imgIndex]?.sFormat, row?.sUserID);
  //         if(resImage){
  //           imageList.push({sUserID: row?.sUserID, image: resImage})
  //         }
  //       });
  //       setUserImages(imageList);
  //     }
  //   }
  // }, [rows]);
  
  const getColumnCellId = React.useCallback((item, params) =>
    buildDataGridCellId({
      gridName: formdata?.component?.sName,
      columnId: item?.sColumnID,
      row: params?.row,
      primaryKey: data?.form?.sPrimaryKey,
    }), [formdata?.component?.sName, data?.form?.sPrimaryKey]);

  const columns = React.useMemo(() => {
  const cols = [];
  formdata?.fixcolumns?.forEach((item, index) => {
    if(item.sType == "avatar") {

        cols.push({
          field: item.sColumnID,
          headerName: item.sHeader,
          width: item.iWidth,
          editable: Boolean(item.bEditable),
          sortable: Boolean(item.bSortable),
          filterable: Boolean(item.bFilterable),
          hideable: Boolean(item.bHideable),

          renderCell: (params) => (
            <div id={getColumnCellId(item, params)}>
              <AvatarCell
                row={params.row}
                item={item}
                token={token}
                stringAvatar={stringAvatar}
              />
            </div>
          )
        });
    } else if(item.sType == "image") {

      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: Boolean(item.bEditable),
        sortable: Boolean(item.bSortable),
        filterable: Boolean(item.bFilterable),
        hideable: Boolean(item.bHideable),

        renderCell: (params) => (
          <div id={getColumnCellId(item, params)}>
            <ImageCell
              imageIds={getNestedValue(params.row, item.sColumnID)}
              format={item.sFormat}
              token={token}
            />
          </div>
        )
      });
    } else if (item.sType == "date") {
      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: item.bEditable,
        type: item.sType,
        sortable: item.bSortable,
        valueGetter: params => new Date(params.value),
        renderCell: params => (
          <div id={getColumnCellId(item, params)}>
            <Typography>
              {formatDate(params.value, item.sFormat)}
            </Typography>
          </div>
        )
      });
    } else if (item.sType == "string") {
      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: item.bEditable,
        type: item.sType,
        sortable: item.bSortable,
        renderCell: (params, ii) => {
          let sysObj = findObjectByFieldValue(params.value, item.sColumnID);
          const parsedData = sysObj?.sFieldFormat ? JSON.parse(sysObj?.sFieldFormat) : {};
          const Icons = MUIICon[sysObj?.sStatusIcon];
          return (
            <>
              <div id={getColumnCellId(item, params)}>
                <Typography {...parsedData}>
                  {" "}
                  {sysObj?.sStatusIcon && <Icons />}{" "}
                  <span dangerouslySetInnerHTML={{ __html: params.value }} />
                </Typography>
              </div>
            </>
          );
        }
      });
    } else if (item.sType == "stringpos") {
      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: item.bEditable,
        type: item.sType,
        sortable: item.bSortable,
        renderCell: (params, ii) => {
          let sysObj = findObjectByFieldValue(params.value, item.sColumnID);
          const parsedData = sysObj?.sFieldFormat ? JSON.parse(sysObj?.sFieldFormat) : {};
          const Icons = MUIICon[sysObj?.sStatusIcon];
          const [sliceStart, sliceEnd] = item.sFormat?.split(":");
       
          return (
            <>
              <div id={getColumnCellId(item, params)}>
                <Typography {...parsedData}>
                  {" "}
                  {sysObj?.sStatusIcon && <Icons />}{" "}
                  <span dangerouslySetInnerHTML={{ __html: params.value?.slice(sliceStart, sliceEnd) }} />
                </Typography>
              </div>
            </>
          );
        }
      });
    } else if (item.sType == "number") {
      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: item.bEditable,
        type: item.sType,
        sortable: item.bSortable,
        renderCell: params => {
          const data = sprintf(item.sFormat || "", params.value || "");
          const formatter = new Intl.NumberFormat("en-US", {
            currency: "USD",
            minimumFractionDigits: 2
          });
          return (
            <>
              <div id={getColumnCellId(item, params)}>
                {" "}
                <Typography>{formatter.format(data)}</Typography>
              </div>
            </>
          );
        }
      });
    } else if(item.sType == "chip") {
      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: item.bEditable,
        type: item.sType,
        sortable: item.bSortable,
        renderCell: params => {
          let color = "";
          const colorObj = format?.data?.records?.filter(
            (field) => field.sFieldName == item.sColumnID
          ).filter((field) => field.sFieldValue == params.value)?.[0];
          if(colorObj){
            color = colorObj ? JSON.parse(colorObj?.sFieldFormat)?.sx?.[0]?.color : {}
          }
          return (
            <div id={getColumnCellId(item, params)}>
              {params?.value && <Chip label={params.value} variant={item?.sFormat} color={color ? (color?.split(".")?.[0] || "default") : "default"} />}
            </div>
          );
        }
      });
    } else if (item.sType == "query") {

          cols.push({
              field: item.sColumnID,
              headerName: item.sHeader,
              width: item.iWidth,
              editable: Boolean(item.bEditable),
              sortable: Boolean(item.bSortable),
              filterable: Boolean(item.bFilterable),
              hideable: Boolean(item.bHideable),

              renderCell: (params) => (
                <div
                  id={getColumnCellId(item, params)}
                >
                  <QueryCell
                    row={params.row}
                    item={item}
                    primaryKey={data?.form?.sPrimaryKey}
                    token={token}
                  />
                </div>
              )
            });
        }
    else {
      cols.push({
        field: item.sColumnID,
        headerName: item.sHeader,
        width: item.iWidth,
        editable: item.bEditable,
        type: item.sType,
        sortable: item.bSortable,
        renderCell: params => {
          return (
            <div id={getColumnCellId(item, params)}>
              {" "}
              <Typography>{params.value}</Typography>
            </div>
          );
        }
      });
    }
  });
    Object.values(formdata.data.sAction).some((item) => item !== "") &&
      cols.push({
        field: "actions",
        headerName: "Actions",
        width: 250,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const items = [];
          if (formdata.data.sAction.actionEdit) {
            const editVerification = editDisabledStatus.find(
              editRow => editRow.id == getRowVerificationId(params.row)
            );
            const disabledEdit =
              isEDITRestricted ||
              (isVerifyConfigured(formdata.data.sAction.actionEditVerify) &&
                editVerification?.status !== false);

            items.push(
              <IconButton
                key="edit"
                size="small"
                disabled={disabledEdit}
                onClick={() => {
                  setFreeFormTabbleEditMainrecord([]);
                  editItem(params.id, params);
                }}
                id={`${formdata?.component?.sName}-edit-${params?.row?.[data?.form?.sPrimaryKey]}`}
              >
                <EditOutlinedIcon
                  sx={{ fontSize: 16 }}
                  color={
                    disabledEdit
                      ? "disabled"
                      : "primary"
                  }
                />
              </IconButton>
            );
          }
          if (formdata.data.sAction.actionView) {
            const viewVerification = viewDisabledStatus.find(
              viewRow => viewRow.id == getRowVerificationId(params.row)
            );
            const disabledView =
              isViewRestricted ||
              (isVerifyConfigured(formdata.data.sAction.actionViewVerify) &&
                viewVerification?.status !== false);

            items.push(
              <IconButton
                key="view"
                size="small"
                disabled={disabledView}
                onClick={() => {
                  if (formdata?.data?.sAction?.altPrimaryKey) {
                    viewItem(params.row[formdata.data.sAction.altPrimaryKey], params);
                  } else {
                    viewItem(params.row[mainFormData.form.sPrimaryKey], params);
                  }
                }}
                id={`${formdata?.component?.sName}-view-${params?.row?.[data?.form?.sPrimaryKey]}`}
              >
                <RemoveRedEyeOutlinedIcon
                  sx={{ fontSize: 16 }}
                  color={
                    disabledView
                      ? "disabled"
                      : "success"
                  }
                />
              </IconButton>
            );
          }
          if(formdata.data.sAction.actionCancel) {
            const cancelVerification = cancelDisabledStatus.find(
              cancelRow => cancelRow.id == getRowVerificationId(params.row)
            );
            const disabledCancel =
              isCANCELRestricted ||
              (isVerifyConfigured(formdata.data.sAction.actionCancelVerify) &&
                cancelVerification?.status !== false);

            items.push(
              <IconButton
                key="cancel"
                size="small"
                disabled={disabledCancel}
                onClick={() => cancelItem(params.id, params)}
                id={`${formdata?.component?.sName}-cancel-${params?.row?.[data?.form?.sPrimaryKey]}`}
              >
                <CancelIcon
                  sx={{ fontSize: 16 }}
                  color={
                    disabledCancel
                      ? "disabled"
                      : "error"
                  }
                />
              </IconButton>
            );
          }
          if (formdata.data.sAction.actionDelete) {
            const deleteVerification = deleteDisabledStatus.find(
              deleteRow => deleteRow.id == getRowVerificationId(params.row)
            );
            const disabledDelete =
              isDELETERestricted ||
              (isVerifyConfigured(formdata.data.sAction.actionDeleteVerify) &&
                deleteVerification?.status !== false);

            items.push(
              <IconButton
                key="delete"
                size="small"
                disabled={disabledDelete}
                onClick={() => deleteItem(params)}
                id={`${formdata?.component?.sName}-delete-${params?.row?.[data?.form?.sPrimaryKey]}`}
              >
                <DeleteIcon
                  sx={{ fontSize: 16 }}
                  color={
                    disabledDelete
                      ? "disabled"
                      : "error"
                  }
                />
              </IconButton>
            );
          }
          if (formdata.data.sAction.actionPrint) {
            const printVerification = printDisabledStatus.find(
              printRow => printRow.id == getRowVerificationId(params.row)
            );
            const disabledPrint =
              isPRINTRestricted ||
              (isVerifyConfigured(formdata.data.sAction.actionPrintVerify) &&
                printVerification?.status !== false);

            const printCounterDataIndex = printCounter.findIndex(
              printRow => printRow.id == getRowVerificationId(params.row)
            );
            let printCounterValue = -1;
            if(printCounterDataIndex != -1){
              printCounterValue = printCounter[printCounterDataIndex]?.count;
            }

            items.push(
              <IconButton
                key="print"
                size="small"
                disabled={disabledPrint}
                onClick={() => printItem(params)}
                id={`${formdata?.component?.sName}-print-${params?.row?.[data?.form?.sPrimaryKey]}`}
              >
                {printCounterValue > 0 ? (
                  <StyledBadge badgeContent={printCounterValue} color="primary">
                    <PrintIcon
                      sx={{
                        fontSize: 16,
                        color: disabledPrint ? "text.disabled" : "text.primary",
                      }}
                    />
                  </StyledBadge>
                ) : (
                  <PrintIcon
                    sx={{
                      fontSize: 16,
                      color: disabledPrint ? "text.disabled" : "text.primary",
                    }}
                  />
                )}
              </IconButton>
            );
          }

          [
            { key: "custom1", action: formdata.data.sAction.actionCustom1, actionType: formdata.data.sAction.actionCustom1Type, actionVerify: formdata.data.sAction.actionCustom1Verify, icon: formdata.data.sAction.actionCustom1Icon, disabledStatus: custom1DisabledStatus },
            { key: "custom2", action: formdata.data.sAction.actionCustom2, actionType: formdata.data.sAction.actionCustom2Type, actionVerify: formdata.data.sAction.actionCustom2Verify, icon: formdata.data.sAction.actionCustom2Icon, disabledStatus: custom2DisabledStatus },
            { key: "custom3", action: formdata.data.sAction.actionCustom3, actionType: formdata.data.sAction.actionCustom3Type, actionVerify: formdata.data.sAction.actionCustom3Verify, icon: formdata.data.sAction.actionCustom3Icon, disabledStatus: custom3DisabledStatus },
          ].forEach(({ key, action, actionType, actionVerify, icon, disabledStatus }) => {
            if (action) {
              const pkLookup = getRowVerificationId(params.row);
              const verification = disabledStatus.find(r => r.id == pkLookup);
              const disabled =
                isVerifyConfigured(actionVerify) &&
                verification?.status !== false;
              const IconComp = icon?.sIcon ? MUIICon[icon.sIcon] : null;
              const { sx: iconSx, ...iconStyleProps } = icon?.sProps || {};
              items.push(
                <IconButton
                  key={key}
                  size="small"
                  disabled={disabled}
                  onClick={() => customItem(params, actionType, action, key)}
                  id={`${formdata?.component?.sName}-${key}-${params?.row?.[data?.form?.sPrimaryKey]}`}
                >
                  {IconComp ? (
                    <IconComp sx={{ ...iconStyleProps, ...iconSx, ...(disabled ? { color: "text.disabled" } : {}) }} />
                  ) : null}
                </IconButton>
              );
            }
          });

          return <div style={{ display: "flex", gap: 4 }}>{items}</div>;
        },
      });

      if(formdata?.data?.sAction?.actionPrintType){
        cols.push({
          field: 'printCount',
          headerName: 'Print Count',
          type: 'number',
          filterable: true,
          hide: true,
        });
      }

      return cols;
  }, [
    formdata?.fixcolumns,
    formdata?.data?.sAction,
    formdata?.data?.sVariables,
    globalvariables,
    format,
    token,
    rows,
    editDisabledStatus,
    viewDisabledStatus,
    cancelDisabledStatus,
    deleteDisabledStatus,
    printDisabledStatus,
    custom1DisabledStatus,
    custom2DisabledStatus,
    custom3DisabledStatus,
    isEDITRestricted,
    isViewRestricted,
    isCANCELRestricted,
    isDELETERestricted,
    isPRINTRestricted,
    getColumnCellId
  ]);


  useEffect(() => {
    if(!shouldUseGridData){
      setSelectFilter(filter1[0]?.sValue);
    }
  }, [filter1, pathname]);

  useEffect(() => {
    const dataAware = formdata?.data?.sDataAware;
    const keys = dataAware
      ? dataAware.match(/{(.*?)}/g)?.map(k => k.replace(/[{}]/g, "")) || []
      : [];

    const allKeysExist = keys.length > 0 && keys.every(k => textValue[k]);

    if (keys.length > 0) {
      if (allKeysExist) {
        fetchDataHandleDataGrid();
      } else {
        setRows([]);
      }
    } else {
      if (!dataGridModalOpen) {
        fetchDataHandleDataGrid();
        if (
          (formdata?.component?.sName === dataGridOptions?.dataGrid &&
            dataGridOptions?.searchedText) ||
          searchedText
        ) {
          fetchSearchedQueryData(
            dataGridOptions?.searchedText || searchedText
          );
        }
      }
    }
  }, [
    gridIdentity,
    formdata?.data?.sDataSource,
    ...(
      formdata?.data?.sDataAware?.match(/{(.*?)}/g)?.map(k => textValue[k.replace(/[{}]/g, "")]) || []
    ),
    currentPage,
    pageSizeLimit,
    dataGridModalOpen
  ]);

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.setPage(currentPage - 1);
      apiRef.current.setPageSize(pageSizeLimit);
    }
  }, [currentPage, pageSizeLimit]);

  useEffect(() => {
    if (apiRef.current) {
      const nextQuickFilterValues = searchedText ? [searchedText] : [];
      const currentQuickFilterValues =
        apiRef.current.state?.filter?.filterModel?.quickFilterValues || [];
      const quickFilterChanged =
        currentQuickFilterValues.length !== nextQuickFilterValues.length ||
        currentQuickFilterValues.some(
          (value, index) => value !== nextQuickFilterValues[index]
        );

      if (quickFilterChanged) {
        ignoreNextFilterChange.current = true;
        apiRef.current.setQuickFilterValues(nextQuickFilterValues);
      }
    }
  }, [searchedText]);

  useEffect(() => {
    const owner = gridRefreshOwnerRef.current;

    setFetchDataHandleDataGrid({
      owner,
      gridIdentity,
      route: pathname,
      fetchDataHandleDataGrid: () => fetchDataHandleDataGrid(),
      uri: selectFilter || formdata.data.sDataSource
    });

    return () => {
      // Only remove this callback if it is still the registered owner. A new
      // route/grid may already have replaced it during React reconciliation.
      setFetchDataHandleDataGrid(current =>
        current?.owner === owner ? {} : current
      );
    };
  }, [
    gridIdentity,
    pathname,
    selectFilter,
    currentPage,
    pageSizeLimit,
    searchedText,
    searchCurrentPage,
    formdata.data.sDataSource
  ]);

  useEffect(() => {
    if(searchedText){
      fetchSearchedQueryData(searchedText);
    }
  }, [searchCurrentPage]);

  const handleRowSelection = (selectionModelArray, currentPageRows) => {
    if (ignoreProgrammaticSelectionClearRef.current) return;

    const selectedRows = mergeDataGridPageSelection(
      selectedBulkRows,
      currentPageRows,
      selectionModelArray
    );
    const selectedIds = selectedRows.map(row => row.id);

    setSelectedDataGridRowIds(selectedIds);
    setMultiDocumentSelectData(selectedRows);
    setselectedRowsDataGrid(selectedRows);
    setSelectedBulkRows(selectedRows);
    const pkValues = selectedRows
      .map(row => getRowVerificationId(row))
      .filter(value => value !== undefined && value !== null && value !== "");
    const bulkPrimaryKeyValues = pkValues?.length > 1 ? pkValues.join(",") : "";
    setRowSelectionModel(bulkPrimaryKeyValues);
    setSelectedBulkPKValues(bulkPrimaryKeyValues);
  };

  function findObjectByFieldValue(fieldValue, columnName) {
    const foundObject = format?.data?.records?.find(
      item => item.sFieldValue === fieldValue && item.sFieldName == columnName
    );
    return foundObject || {};
  }

  async function fetchSearchedQueryData(searchText) {
    if(!searchText || searchText == ""){
      setSearchCurrentPage(1);
      setSearchedRows([]);
      setCurrentPage(1);
      fetchDataHandleDataGrid();
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const columnKeys = formdata?.fixcolumns?.map((clm) => clm.sColumnID)?.join(",");

    const uri = `${serverAddress}${replaceUriParams(textValue, formdata?.component?.pagination?.search)}`;
    let apiURI = "";
    if(uri.includes("?")){
      apiURI = `${uri}&term=${searchText}&fields=${columnKeys}&page=${searchCurrentPage}&limit=${pageSizeLimit}`;
    } else {
      apiURI = `${uri}?term=${searchText}&fields=${columnKeys}&page=${searchCurrentPage}&limit=${pageSizeLimit}`;
    }

    const gridRequest = beginGridRequest(apiURI, "search");
    let response;

    try {
      response = await axios.get(
        apiURI,
        {
          signal: gridRequest.controller.signal,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!isCurrentGridRequest(gridRequest)) {
        logGridRequestCompletion(gridRequest, response, null, false);
        return;
      }

      logGridRequestCompletion(gridRequest, response, null, true);
      if (!Array.isArray(response?.data?.data?.records)) {
        throw new Error("DataGrid search response does not contain a records array.");
      }

      setTotalRows(String(response?.data?.data?.totalRows));
      if(filter1 && filter1.length > 0){
        setSelectFilter(filter1[0]?.sValue);
      }

      const searchRows = prepareDataGridRows(
        response.data.data.records,
        `search_page_${searchCurrentPage}`
      ).map(item => {
        Object.keys(item)?.forEach((itemKey) => {
          if(typeof item[itemKey] == "number"){
            item[itemKey] = String(item[itemKey]);
          }
        })
        return item;
      });
      verifyRowActions(searchRows, gridRequest.verificationGeneration);
      setSearchedRows(searchRows);
    } catch (error) {
      if (!isCurrentGridRequest(gridRequest)) {
        logGridRequestCompletion(gridRequest, response, error, false);
        return;
      }

      logGridRequestCompletion(gridRequest, response, error, false);
      if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return;
      console.error("[DataGridRequest] Search request failed", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg || error?.message || "Unable to search grid data.",
        { domain: "datagrid", action: "search" }
      );
    } finally {
      finishGridRequest(gridRequest);
    }
  }

  function buildFilterQuery(items, logicOperator) {
    let query = `filterCount=${items.length}`;
    
    items.forEach((item, index) => {
      const i = index + 1;
      query += `&f${i}=${encodeURIComponent(item.field)}`
            + `&op${i}=${encodeURIComponent(item.operator)}`
            + `&value${i}=${encodeURIComponent(item.value)}`;
    });

    query += `&conj=${logicOperator}`;
    
    return query;
  }

  async function fetchFilterQueryData(queryString) {
    const uri = `${serverAddress}${replaceUriParams(textValue, formdata?.component?.pagination?.filter)}`;
    let apiURI = "";
    if(uri.includes("?")){
      apiURI = `${uri}${queryString}&page=${searchCurrentPage}&limit=${pageSizeLimit}`;
    } else {
      apiURI = `${uri}?${queryString}&page=${searchCurrentPage}&limit=${pageSizeLimit}`;
    }
    const gridRequest = beginGridRequest(apiURI, "column-filter");
    let response;
    try {
      response = await axios.get(
        apiURI,
        {
          signal: gridRequest.controller.signal,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!isCurrentGridRequest(gridRequest)) {
        logGridRequestCompletion(gridRequest, response, null, false);
        return;
      }

      logGridRequestCompletion(gridRequest, response, null, true);
      if (!Array.isArray(response?.data?.data?.records)) {
        throw new Error("DataGrid column-filter response does not contain a records array.");
      }

      setTotalRows(String(response?.data?.data?.totalRows));
      if(filter1 && filter1.length > 0){
        setSelectFilter(filter1[0]?.sValue);
      }

      const filteredRows = prepareDataGridRows(
        response.data.data.records,
        `column_filter_page_${searchCurrentPage}`
      ).map(item => {
        Object.keys(item)?.forEach((itemKey) => {
          if(typeof item[itemKey] == "number"){
            item[itemKey] = String(item[itemKey]);
          }
        })
        return item;
      });
      verifyRowActions(filteredRows, gridRequest.verificationGeneration);
      setRows(filteredRows);
    } catch (error) {
      if (!isCurrentGridRequest(gridRequest)) {
        logGridRequestCompletion(gridRequest, response, error, false);
        return;
      }

      logGridRequestCompletion(gridRequest, response, error, false);
      if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return;
      console.error("[DataGridRequest] Column-filter request failed", error);
      CustomAlert(
        "error",
        error?.response?.data?.metadata?.msg || error?.message || "Unable to filter grid data.",
        { domain: "datagrid", action: "filter" }
      );
    } finally {
      finishGridRequest(gridRequest);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.querySelector('.MuiInputBase-input[type="search"]');
      if (input && !input.id.includes(`${formdata?.component?.sName}-search`)) {
        input.id = `${formdata?.component?.sName}-search`;
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [apiRef, formdata]);

  const bulkActionEnabled =
  formdata?.data?.sBulkAction?.sActions?.enabled === true;
  const bulkConfiguration = formdata?.data?.sBulkAction?.sActions || {};
  const workflowVerificationByType = {
    edit: {
      configured: isVerifyConfigured(formdata?.data?.sAction?.actionEditVerify),
      statuses: editDisabledStatus
    },
    cancel: {
      configured: isVerifyConfigured(formdata?.data?.sAction?.actionCancelVerify),
      statuses: cancelDisabledStatus
    },
    delete: {
      configured: isVerifyConfigured(formdata?.data?.sAction?.actionDeleteVerify),
      statuses: deleteDisabledStatus
    },
    print: {
      configured: isVerifyConfigured(formdata?.data?.sAction?.actionPrintVerify),
      statuses: printDisabledStatus
    },
    view: {
      configured: isVerifyConfigured(formdata?.data?.sAction?.actionViewVerify),
      statuses: viewDisabledStatus
    }
  };
  const isBulkActionWorkflowAllowed = action => {
    const verificationType = resolveBulkWorkflowVerification(action, bulkConfiguration);
    const verification = workflowVerificationByType[verificationType] || {
      configured: false,
      statuses: []
    };
    return isBulkActionAllowedForRows(action, selectedBulkRows, {
      primaryKey: mainFormData?.form?.sPrimaryKey,
      statusFieldName: statusFieldName || "sDocStatus",
      verifyConfigured: verification.configured,
      verificationStatuses: verification.statuses
    });
  };
  const permittedBulkActions = bulkActions?.filter(action => {
    const requiredActivity = resolveBulkRequiredActivity(action, bulkConfiguration);
    return requiredActivity && activityAccess[requiredActivity] === "ALLOWED";
  }) || [];
  const hasAllowedBulkAction = permittedBulkActions.some(action =>
    isBulkActionWorkflowAllowed(action)
  );
  const bulkSelectionReady = hasBulkSelection(selectedBulkRows);
  
  return (
    <>
      <Grid container alignItems="center">
        <Grid item>
          {formdata?.data?.sFilters?.filter1?.enabled === true ? (
            <Typography
              id={formdata.data.sFilters.filter1.sName + "-filter-label"}
              {...formdata.data.sFilters.filter1.sLabelProps}
            >
              {formdata.data.sFilters.filter1.sLabel}
            </Typography>
          ) : null}
        </Grid>
        <Grid item {...formdata.data.sFilters.filter1.sSelectProps}>
          {formdata?.data?.sFilters?.filter1?.enabled === true ? (
            <Box>
              <FormControl {...formdata.data.sFilters.filter1.sSelectProps}>
                <Select
                  id={formdata.data.sFilters.filter1.sName}
                  onChange={(e, t) => handleFilter1(e.target.value)}
                  value={selectFilter || ""}
                >
                  {filter1?.map(item => (
                    <MenuItem key={item?.sValue} value={item?.sValue}>
                      {item?.sChoice}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </Grid>
      <Grid item>
        {bulkActionEnabled && permittedBulkActions.length > 0 ? (
          <Typography
            id={formdata.data.sBulkAction?.sActions.sName}
            {...formdata.data.sBulkAction?.sActions.sLabelProps}
          >
            {formdata.data.sBulkAction?.sActions.sLabel}
          </Typography>
        ) : null}
      </Grid>
      <Grid item {...formdata.data.sBulkAction?.sActions.sSelectProps}>
        {bulkActionEnabled && permittedBulkActions.length > 0 ? (
          <Box>
            <FormControl {...formdata.data.sBulkAction?.sActions.sSelectProps}>
              <Select
                displayEmpty
                id={formdata.data.sBulkAction?.sActions.sName}
                onChange={(e, t) => handleBuldActionChange(e.target.value)}
                value={selectedBulkAction || ""}
                disabled={!bulkSelectionReady || !hasAllowedBulkAction}
              >
                <MenuItem value="" disabled>
                  <em>Select Bulk Action</em>
                </MenuItem>

                {permittedBulkActions.map(item => (
                  <MenuItem
                    key={item?.sValue}
                    value={item?.sValue}
                    disabled={!isBulkActionWorkflowAllowed(item)}
                  >
                    {item?.sChoice}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : null}
      </Grid>

        {bulkActionEnabled && permittedBulkActions.length > 0 && (
          <Grid
            {...formdata.data.sBulkAction?.sActions?.sButton?.grid_props}
            {...formdata.data.sBulkAction?.sActions?.sButton?.sButtonProps}
          >
            <Button
              id={formdata.data.sBulkAction?.sActions?.sButton?.sButtonName}
              onClick={handleBulkActionClick}
              {...formdata.data.sBulkAction?.sActions?.sButton?.sButtonProps}
              disabled={
                !bulkSelectionReady ||
                !selectedBulkAction ||
                !isBulkActionWorkflowAllowed(
                  permittedBulkActions.find(item => item?.sValue === selectedBulkAction)
                )
              }
            >
              {formdata.data.sBulkAction?.sActions?.sButton?.sButtonLabel}
            </Button>
          </Grid>
        )}
        <Grid item>
          {formdata?.data?.sFilters?.filter2?.enabled === true ? (
            <Typography {...formdata.data.sFilters.filter2.sLabelProps}>
              {formdata.data.sFilters.filter2.sLabel}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={1}>
          {formdata?.data?.sFilters?.filter2?.enabled === true ? (
            <Box>
              <FormControl {...formdata.data.sFilters.filter2.sSelectProps}>
                <Select
                  id={formdata.data.sFilters.filter2.sName}
                  value={selectFilter2}
                  onChange={(e) => handleFilter2(e.target.value)}
                >
                  {filter2?.map(item => (
                    <MenuItem key={item?.sValue} value={item?.sValue}>{item?.sChoice}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </Grid>
        <Grid item>
          {formdata?.data?.sFilters?.filter3?.enabled === true ? (
            <Typography {...formdata.data.sFilters.filter3.sLabelProps}>
              {formdata.data.sFilters.filter3.sLabel}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={1}>
          {formdata?.data?.sFilters?.filter3?.enabled === true ? (
            <Box>
              <FormControl {...formdata.data.sFilters.filter3.sSelectProps}>
                <Select id={formdata.data.sFilters.filter3.sName} defaultValue={"All"}>
                  {filter3?.map(item => (
                    <MenuItem value={item?.sValue}>{item?.sChoice}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </Grid>
        <Grid item>
          {formdata?.data?.sFilters?.filter4?.enabled === true ? (
            <Typography {...formdata.data.sFilters.filter4.sLabelProps}>
              {formdata.data.sFilters.filter4.sLabel}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={1}>
          {formdata?.data?.sFilters?.filter4?.enabled === true ? (
            <Box>
              <FormControl {...formdata.data.sFilters.filter4.sSelectProps}>
                <Select id={formdata.data.sFilters.filter4.sName} defaultValue={"All"}>
                  {filter3?.map(item => (
                    <MenuItem value={item?.sValue}>{item?.sChoice}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </Grid>
      </Grid>
      <Box
        sx={{...formdata.component?.options?.sParentContainerProps.style, overflowY: "scroll"}}
        {...formdata?.component?.options?.others1}
      >
        <div id={formdata.component.sName}>
          {isSearching ?
           <DataGridPro
            pagination
            apiRef={apiRef}
            id={formdata.component.sName}
            autoHeight={false}
            disableAutosize={false}
            // slots={
            //   (formdata?.component?.sProps?.slotProps &&
            //     Object.keys(formdata?.component?.sProps?.slotProps).length > 0)
            //     ? {
            //         toolbar: GridToolbar,
            //         loadingOverlay: progress ? CustomLoadingOverlay : "LinearProgress",
            //         baseCheckbox: RowCheckbox,
            //       }
            //     : {
            //         baseCheckbox: RowCheckbox,
            //       }
            // }
            slots={
  (formdata?.component?.sProps?.slotProps &&
    Object.keys(formdata?.component?.sProps?.slotProps).length > 0)
    ? {
        toolbar: GridToolbar,
        loadingOverlay: progress ? CustomLoadingOverlay : "LinearProgress",
        baseCheckbox: RowCheckbox,
        pagination: () => <CustomPagination apiRef={apiRef} totalRows={totalRows} pageSizeLimit={pageSizeLimit} />,  // <-- ADD THIS LINE
      }
    : {
        baseCheckbox: RowCheckbox,
        pagination: () => <CustomPagination apiRef={apiRef} totalRows={totalRows} pageSizeLimit={pageSizeLimit} />,  // <-- ADD THIS LINE
      }
}
            slotProps={formdata?.component?.sProps?.slotProps}
            rows={searchedRows}
            loading={progress}
            columns={columns}
            {...formdata?.component?.sProps}
            rowSelectionModel={selectedDataGridRowIds}
            keepNonExistentRowsSelected
            onRowSelectionModelChange={newRowSelectionModel => {
              handleRowSelection(newRowSelectionModel, searchedRows);
            }}
            filterMode={formdata?.component?.pagination?.type === "server" ? "server" : "client"}
            onFilterModelChange={(model) => {
              if (ignoreNextFilterChange.current) {
                ignoreNextFilterChange.current = false;
                return;
              }
              if (formdata?.component?.pagination?.type === "server") {
                const searchText = model.quickFilterValues?.join(" ") || "";
                setSearchedText(searchText);
                fetchSearchedQueryData(searchText);
              }
            }}
            {...(formdata?.component?.pagination?.type === "server" && {
              paginationMode: formdata?.component?.pagination?.type,
              initialState: {
                pagination: {
                  paginationModel: { pageSize: pageSizeLimit, page: 0 },
                },
                columns: {
                  columnVisibilityModel: {
                    printCount: false
                  }
                }
              },
              pageSizeOptions: JSON.parse(formdata?.component?.pagination?.rowsPerPageOptions?.replaceAll("{", "")?.replaceAll("}", "")),
              onPaginationModelChange: (model) => {
                setSearchCurrentPage(model.page + 1);
                setPageSizeLimit(model.pageSize);
              },
              rowCount: Number(totalRows) || 0
            })}
            disableRowSelectionOnClick
            />
          : <DataGridPro
            pagination
            disableRowSelectionOnClick
            apiRef={apiRef}
            id={formdata.component.sName}
            autoHeight={false}
            disableAutosize={false}
            // slots={
            //   (formdata?.component?.sProps?.slotProps &&
            //     Object.keys(formdata?.component?.sProps?.slotProps).length > 0)
            //     ? {
            //         toolbar: GridToolbar,
            //         loadingOverlay: progress ? CustomLoadingOverlay : "LinearProgress",
            //         baseCheckbox: RowCheckbox,
            //       }
            //     : {
            //         baseCheckbox: RowCheckbox,
            //       }
            // }
            slots={
              (formdata?.component?.sProps?.slotProps &&
                Object.keys(formdata?.component?.sProps?.slotProps).length > 0)
                ? {
                    toolbar: GridToolbar,
                    loadingOverlay: progress ? CustomLoadingOverlay : "LinearProgress",
                    baseCheckbox: RowCheckbox,
                    pagination: () => <CustomPagination apiRef={apiRef} totalRows={totalRows} pageSizeLimit={pageSizeLimit} />,  // <-- ADD THIS LINE
                  }
                : {
                    baseCheckbox: RowCheckbox,
                    pagination: () => <CustomPagination apiRef={apiRef} totalRows={totalRows} pageSizeLimit={pageSizeLimit} />,  // <-- ADD THIS LINE
                  }
            }
            slotProps={{...formdata?.component?.sProps?.slotProps, toolbar: { ...formdata?.component?.sProps?.slotProps?.toolbar, showQuickFilter: true }}}
            rows={rows}
            loading={progress}
            columns={columns}
            {...formdata?.component?.sProps}
            rowSelectionModel={selectedDataGridRowIds}
            keepNonExistentRowsSelected
            onRowSelectionModelChange={newRowSelectionModel => {
              handleRowSelection(newRowSelectionModel, rows);
            }}
            filterMode={formdata?.component?.pagination?.type === "server" ? "server" : "client"}
            onFilterModelChange={(model) => {
              if (ignoreNextFilterChange.current) {
                ignoreNextFilterChange.current = false;
                return;
              }
              if (formdata?.component?.pagination?.type === "server") {
                if(model.quickFilterValues?.length == 0){
                  const queryString = buildFilterQuery(model?.items, model?.logicOperator);
                  fetchFilterQueryData(queryString);
                } else {
                  const searchText = model.quickFilterValues?.join(" ") || "";
                  setSearchedText(searchText);
                  fetchSearchedQueryData(searchText);
                }
              }
            }}
            {...(formdata?.component?.pagination?.type === "server" && {
              paginationMode: formdata?.component?.pagination?.type,
              initialState: {
                pagination: {
                  paginationModel: { pageSize: pageSizeLimit, page: 0 },
                },
                columns: {
                  columnVisibilityModel: {
                    printCount: false
                  }
                }
              },
              pageSizeOptions: JSON.parse(formdata?.component?.pagination?.rowsPerPageOptions?.replaceAll("{", "")?.replaceAll("}", "")),
              onPaginationModelChange: (model) => {
                setCurrentPage(model.page + 1);
                setPageSizeLimit(model.pageSize);
              },
              rowCount: Number(totalRows) || 0
            })}
            />}
           </div>
      </Box>
    </>
  );
}
